import { CloudUpload } from '@mui/icons-material';
import {
  Box,
  Typography,
  useColorScheme,
  CircularProgress,
} from '@mui/material';

import { DropFileProps } from './types';

const DropFile = ({
  isError,
  onClick,
  isLoading,
  width = '100%',
  height = '100%',
  allowedFileSize = 10,
}: DropFileProps) => {
  const { mode, systemMode } = useColorScheme();
  const isDarkMode =
    mode === 'system' ? systemMode === 'dark' : mode === 'dark';
  return (
    <Box
      onClick={onClick}
      sx={{
        width,
        height,
        border: 2,
        zIndex: 1,
        display: 'flex',
        borderRadius: 1,
        cursor: 'pointer',
        userSelect: 'none',
        alignItems: 'center',
        position: 'relative',
        borderStyle: 'dashed',
        flexDirection: 'column',
        color: 'text.secondary',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
        pointerEvents: isLoading ? 'none' : 'auto',
        borderColor: isError
          ? 'error.main'
          : isDarkMode
            ? 'grey.600'
            : 'grey.300',
        '& .upload-icon': {
          fontSize: 40,
          mb: isError ? 3 : isLoading ? 0 : 1,
          color: isDarkMode ? 'primary.light' : 'primary.main',
        },
        backgroundColor: isError
          ? isDarkMode
            ? 'error.dark'
            : 'error.light'
          : isDarkMode
            ? 'grey.800'
            : 'grey.50',
        '&:hover': {
          borderColor: isError
            ? isDarkMode
              ? 'error.light'
              : 'error.dark'
            : 'primary.main',
          backgroundColor: isError
            ? isDarkMode
              ? 'error.main'
              : 'error.main'
            : isDarkMode
              ? 'grey.700'
              : 'grey.100',
        },
      }}
    >
      {isLoading && !isError ? (
        <CircularProgress
          size={24}
          sx={{
            color: isDarkMode ? 'primary.light' : 'primary.main',
          }}
        />
      ) : (
        <>
          <CloudUpload className="upload-icon" />

          {isError && (
            <Typography
              variant="body2"
              sx={{
                mb: 2,
                zIndex: 10,
                fontWeight: 500,
                textAlign: 'center',
              }}
            >
              The file was not uploaded
            </Typography>
          )}

          <Typography
            variant="body2"
            sx={{
              fontWeight: 400,
              textAlign: 'center',
              '& span': {
                fontWeight: 600,
                cursor: 'pointer',
                textDecoration: 'underline',
                color: isDarkMode ? 'primary.light' : 'primary.main',
                '&:hover': {
                  color: isDarkMode ? 'primary.main' : 'primary.dark',
                },
              },
            }}
          >
            Drag your file here, or <span>browse</span>
          </Typography>

          {!isError && (
            <Box
              sx={{
                mt: 1,
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                justifyContent: 'center',
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  lineHeight: 1.2,
                  textAlign: 'center',
                  color: isDarkMode ? 'grey.400' : 'text.secondary',
                }}
              >
                File size cannot
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  lineHeight: 1.2,
                  textAlign: 'center',
                  color: isDarkMode ? 'grey.400' : 'text.secondary',
                }}
              >
                be more than {allowedFileSize} MB
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export { DropFile };
